'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Title, Text, Group, Box } from '@mantine/core'
import { ChevronDownIcon } from '@/components/Icons/ChevronDownIcon'
import { parseHtml } from '@/lib/parseHtml'

const MotionChevronDownIcon = motion(ChevronDownIcon)

export interface NumberedAccordionItemProps {
    number: number
    title: string
    content: string
    isOpen: boolean
    onToggle: () => void
    /** Hide top border (e.g. for first item in a list). Default: false */
    hideTopBorder?: boolean
    /** Hide the number (e.g. for FAQ-style accordions). Default: false */
    showNumber?: boolean
    /**
     * Optional content before the title (e.g. icons). When set, the number is not shown.
     * Use with `showNumber={false}` and set `contentStyle.ml` to align body with the title column.
     */
    leadingSlot?: React.ReactNode
    /** Content typography overrides. Default: size md, fw 400, lh 140%, ml 30 (or 0 when showNumber=false) */
    contentStyle?: {
        fz?: React.CSSProperties['fontSize'] | 'xs' | 'sm' | 'md' | 'lg' | 'xl'
        fw?: number
        lh?: string
        ml?: number
        pt?: number
    }
    /** When true, content is always visible, no toggle behavior, no chevron. */
    alwaysOpen?: boolean
    /** Override the title font-weight. Defaults to 550 (open) / 500 (closed). */
    titleFontWeight?: number
}

export default function NumberedAccordionItem({
    number,
    title,
    content,
    isOpen,
    onToggle,
    hideTopBorder,
    showNumber = true,
    leadingSlot,
    contentStyle,
    alwaysOpen = false,
    titleFontWeight,
}: NumberedAccordionItemProps) {
    const { fz = 'md', fw = 400, lh = '140%', ml = showNumber ? 30 : 0, pt = 0 } = contentStyle ?? {}

    const effectiveOpen = alwaysOpen || isOpen
    const resolvedTitleFw = titleFontWeight ?? (effectiveOpen ? 550 : 500)

    return (
        <Box
            pb={24}
            pt={24}
            style={{
                borderTop: hideTopBorder ? undefined : '1px solid rgba(0, 0, 0, 0.15)',
                overflow: 'visible',
            }}
        >
            <Group
                onClick={alwaysOpen ? undefined : onToggle}
                justify="space-between"
                wrap="nowrap"
                gap={16}
                style={{ cursor: alwaysOpen ? 'default' : 'pointer' }}
            >
                <Group gap="lg" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                    {leadingSlot ? (
                        <Box style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{leadingSlot}</Box>
                    ) : (
                        showNumber && (
                            <Text
                                c="gray.9"
                                fz={24}
                                fw={resolvedTitleFw}
                                lh="100%"
                                lts="-0.24px"
                                style={{ flexShrink: 0 }}
                            >
                                {number}
                            </Text>
                        )
                    )}
                    <Title order={3} c="gray.9" fz={24} fw={resolvedTitleFw} lh="100%" lts="-0.24px" m={0} ta="left">
                        {title}
                    </Title>
                </Group>
                {!alwaysOpen && (
                    <MotionChevronDownIcon
                        animate={{ rotate: effectiveOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        style={{
                            flexShrink: 0,
                            transformOrigin: 'center',
                        }}
                    />
                )}
            </Group>
            <motion.div
                initial={false}
                animate={{
                    height: effectiveOpen ? 'auto' : 0,
                    opacity: effectiveOpen ? 1 : 0,
                }}
                transition={{ duration: alwaysOpen ? 0 : 0.3, ease: 'easeInOut' }}
                style={{ overflow: 'hidden' }}
            >
                <Text
                    component="div"
                    fz={fz}
                    fw={fw}
                    lh={lh}
                    c="gray.9"
                    ml={ml}
                    pt={pt}
                    styles={{
                        root: {
                            '& b, & strong': { fontWeight: 600 },
                            '& p': { margin: 0, marginBottom: '0.5em' },
                            '& p:last-child': { marginBottom: 0 },
                        },
                    }}
                >
                    {parseHtml(content)}
                </Text>
            </motion.div>
        </Box>
    )
}
